<template>
    <div class="drawer" @click="handleOutsideEvent">
        <FocusTrap :escapeDeactivates="false" :fallbackFocus="fallbackFocusSelector">
            <div class="drawer-box" :class="[position && `position_${position}`]">
                <dialog
                    ref="dialogRef"
                    class="drawer-box-inner"
                    :class="[`position_${position}`, `size_${size}`]"
                    tabindex="-1"
                    @click.stop>
                    <div class="drawer-box-header" :class="[`padding_${padding}`]">
                        <slot v-if="slots.header" name="header" />
                        <Header
                            v-else
                            :onClose="onClose && handleClose"
                            :onBack="onBack && handleBack"
                            :title="title"
                            :subTitle="subTitle" />
                    </div>
                    <div class="drawer-box-slots">
                        <div
                            v-if="slots.default"
                            class="drawer-box-content"
                            :class="[`padding_${padding}`]">
                            <slot />
                        </div>
                        <div
                            v-if="slots.footer"
                            class="drawer-box-footer"
                            :class="[`padding_${padding}`]">
                            <slot name="footer" />
                        </div>
                    </div>
                </dialog>
            </div>
        </FocusTrap>
    </div>
</template>

<script lang="ts">
    import {
        defineComponent,
        onMounted,
        onBeforeUnmount,
        PropType,
        useSlots,
        nextTick,
        ref,
    } from 'vue';
    import { Position, Padding, Size } from './Drawer.interfaces';
    import { useDialogManager } from '@shared/helpers/dialogManager';
    import { FocusTrap } from 'focus-trap-vue';
    import Header from '@ui/components/Drawer/Header/Header.vue';

    export default defineComponent({
        name: 'Drawer',
        components: { Header, FocusTrap },
        props: {
            position: {
                type: String as PropType<Position>,
                default: 'right',
            },
            size: {
                type: String as PropType<Size>,
                default: 'm',
            },
            title: String,
            subTitle: {
                type: String as PropType<string | null>,
            },
            lockBody: {
                type: Boolean,
                default: true,
            },
            padding: {
                type: String as PropType<Padding>,
                default: 'm',
            },
            onBack: Function,
            onOpen: Function,
            onClose: Function,
            onCloseOutside: {
                type: Boolean,
                default: true,
            },
        },
        setup(props) {
            const slots = useSlots();
            const dialogManager = useDialogManager();
            const dialogRef = ref<HTMLDialogElement | null>(null);

            const fallbackFocusSelector = () => dialogRef.value || document.body;

            onMounted(() => {
                dialogManager.register({
                    lockBody: props.lockBody,
                    onEscClose: handleOutsideEvent,
                });

                nextTick(handleOpen);
            });

            onBeforeUnmount(() => {
                dialogManager.unregister();
            });

            const handleOpen = () => {
                props.onOpen && props.onOpen();
            };

            const handleClose = () => {
                props.onClose && props.onClose();
            };

            const handleBack = () => {
                props.onBack && props.onBack();
            };

            const handleOutsideEvent = () => {
                props.onCloseOutside && handleClose();
            };

            return {
                slots,
                dialogRef,
                fallbackFocusSelector,
                handleClose,
                handleBack,
                handleOutsideEvent,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Drawer';
</style>
